import React, { Component } from 'react';
import {
    Typography,
    Box,
    Paper,
    withStyles
} from '@material-ui/core';
import { Alert } from "@mui/material";
import CustomButton from "../common/customButton";

const styles = theme => ({
    sectionBody: {
        padding: '1rem',
    },
    valorMonetario: {
        fontFamily: 'monospace',
        fontWeight: 'bold',
    },
    equipamentCard: {
        backgroundColor: '#DDF2DA',
        marginBottom: '1rem',
        padding: '0.5rem',
        borderRadius: '0.375rem',
        boxShadow: 'none',
    },
    equipamentCardClientBackground: {
        backgroundColor: '#f2e8da', // Client view uses slightly different color
    },
    equipamentCardTitle: {
        marginTop: 0,
        marginBottom: '1rem',
        fontSize: '1rem',
        color: '#212529',
        fontWeight: 500,
    },
    bodyText: {
        fontSize: '1rem',
        color: '#212529',
    },
    bodyTextWithMargin: {
        fontSize: '1rem',
        color: '#212529',
        marginBottom: '1rem',
    },
    bodyTextSmall: {
        fontSize: '0.875rem',
        color: '#212529',
    },
    displayEndText: {
        display: 'flex',
        justifyContent: 'end',
        marginTop: '0.25rem',
    },
    itemGroup: {
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(1),
        borderRadius: 4,
        marginBottom: theme.spacing(1),
    },
    clientActionButtons: {
        marginTop: '1.5rem',
        display: 'flex',
        gap: 12,
        justifyContent: 'center',
        flexWrap: 'wrap',
    },
    emailAlert: {
        marginTop: '1rem',
        marginBottom: '1rem',
    },
});

/**
 * DetailsOrcamentoSection - Shared component for displaying orçamento details
 * 
 * Props:
 * - orcamento: object - The budget data
 * - modo: string - "cliente" | "workshop" | "admin" - Controls which buttons are shown
 * - lojaEmail: string - Store email for purchase orders (client view)
 * 
 * Client callbacks:
 * - onAceitarOrcamento(id): Called when "Aceitar" is clicked
 * - onRecusarOrcamento(id): Called when "Recusar" is clicked
 */
class DetailsOrcamentoSection extends Component {
    formatarMoeda = (valor) => {
        if (valor == null || valor === '') return '-';
        return Number(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    // Client-specific buttons for pending approval
    renderClientButtons = () => {
        const { classes, orcamento, onAceitarOrcamento, onRecusarOrcamento } = this.props;
        const status = orcamento?.status?.toLowerCase();

        if (status === 'pendente de aprovação') {
            return (
                <Box className={classes.clientActionButtons}>
                    <CustomButton
                        variant="greenFilled"
                        size="medium"
                        onClick={() => onAceitarOrcamento && onAceitarOrcamento(orcamento.id)}
                    >
                        Aceitar
                    </CustomButton>
                    <CustomButton
                        variant="redFilled"
                        size="medium"
                        onClick={() => onRecusarOrcamento && onRecusarOrcamento(orcamento.id)}
                    >
                        Recusar
                    </CustomButton>
                </Box>
            );
        }

        return null;
    };



    // Email alert for client after viewing budget
    renderEmailAlert = () => {
        const { classes, orcamento, lojaEmail, modo } = this.props;

        // Only show for client view and when budget exists
        if (modo !== 'cliente' || !orcamento?.id) return null;

        const email = lojaEmail || 'contato@empresa.com'; // Fallback email

        return (
            <Alert severity="warning" className={classes.emailAlert}>
                <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                    ATENÇÃO
                </Typography>
                <Typography variant="body2">
                    Após a aprovação deste orçamento, ordens de compra devem ser encaminhadas para o e-mail:{' '}
                    <strong>{email}</strong> contendo: <strong>Orçamento: #{orcamento.id}</strong> no título para agilizar seu atendimento.
                </Typography>
            </Alert>
        );
    };

    render() {
        const { classes, orcamento, modo } = this.props;

        if (!orcamento) {
            return null;
        }

        // Client view: Hide orçamento when status is "aguardando orçamento"
        const status = orcamento.status?.toLowerCase();
        if (modo === 'cliente' && status === 'aguardando orçamento') {
            return (
                <Typography className={classes.bodyText} color="textSecondary">
                    Nenhum orçamento disponível para visualização.
                </Typography>
            );
        }

        let orcamentoContent = [];

        if (orcamento.itens?.length) {
            const agrupados = {};
            orcamento.itens.forEach(s => {
                if (!agrupados[s.equip_id]) agrupados[s.equip_id] = [];
                agrupados[s.equip_id].push(s);
            });

            Object.entries(agrupados).forEach(([equipId, itens]) => {
                const eq = itens[0];
                const cabecalhoEquip = `${eq.equipamento_nome || "Equipamento"} – ${eq.equipamento_marca || "-"} – ${eq.equipamento_modelo || "-"} – ${eq.equipamento_serie || "-"}`;

                let pecasHtml = [];
                let procedimentosHtml = [];
                let totalPecas = 0, totalProcedimentos = 0;

                itens.forEach(s => {
                    const qtd = Number(s.qtd) || 1;
                    const valor = Number(s.valor) || 0;
                    const total = qtd * valor;
                    const cod = s.codigo_interno || '-';
                    const nome = s.nome || '-';

                    if (s.tipo_item === "peca") {
                        totalPecas += total;
                        pecasHtml.push(
                            <Box key={`${equipId}-peca-${cod}`} display="flex" justifyContent="space-between">
                                <Typography className={classes.bodyText}>
                                    {cod} | {qtd}x {nome}
                                </Typography>
                                <Typography className={classes.bodyText}>
                                    {this.formatarMoeda(total)}
                                </Typography>
                            </Box>
                        );
                    } else if (s.tipo_item === "procedimento") {
                        totalProcedimentos += total;
                        procedimentosHtml.push(
                            <Box key={`${equipId}-proc-${cod}`} display="flex" justifyContent="space-between">
                                <Typography className={classes.bodyText}>
                                    {cod} | {qtd}x {nome}
                                </Typography>
                                <Typography className={classes.bodyText}>
                                    {this.formatarMoeda(total)}
                                </Typography>
                            </Box>
                        );
                    }
                });

                const totalEquip = totalPecas + totalProcedimentos;

                // Use different background color for client view
                const cardClassName = modo === 'cliente'
                    ? `${classes.equipamentCard} ${classes.equipamentCardClientBackground}`
                    : classes.equipamentCard;

                orcamentoContent.push(
                    <Paper key={equipId} className={cardClassName} elevation={1}>
                        <Typography className={classes.equipamentCardTitle}>
                            {cabecalhoEquip.toUpperCase()}
                        </Typography>

                        <Paper className={classes.itemGroup} elevation={0}>
                            <Typography className={classes.bodyTextWithMargin}>
                                <i>Peças:</i>
                            </Typography>
                            {pecasHtml.length > 0 ? pecasHtml : <Typography className={classes.bodyTextSmall}>Nenhuma peça</Typography>}
                            <Box className={classes.displayEndText}>
                                <Typography className={classes.bodyText}>
                                    <strong>Total peças: <span>{this.formatarMoeda(totalPecas)}</span></strong>
                                </Typography>
                            </Box>
                        </Paper>

                        <Paper className={classes.itemGroup} elevation={0}>
                            <Typography className={classes.bodyTextWithMargin}>
                                <i>Procedimentos:</i>
                            </Typography>
                            {procedimentosHtml.length > 0 ? procedimentosHtml : <Typography className={classes.bodyTextSmall}>Nenhum procedimento</Typography>}
                            <Box className={classes.displayEndText}>
                                <Typography className={classes.bodyText}>
                                    <strong>Total procedimentos: <span>{this.formatarMoeda(totalProcedimentos)}</span></strong>
                                </Typography>
                            </Box>
                        </Paper>

                        <Box display="flex" justifyContent="flex-end" mt={1}>
                            <Typography className={classes.bodyText} style={{ fontWeight: 'bold' }}>
                                TOTAL EQUIPAMENTO: <span>{this.formatarMoeda(totalEquip)}</span>
                            </Typography>
                        </Box>
                    </Paper>
                );
            });
        }

        return (
            <Box className={classes.sectionBody}>
                <Typography className={classes.bodyTextWithMargin}>
                    <strong>Status:</strong> {orcamento.status}
                </Typography>
                <Typography className={classes.bodyTextWithMargin}>
                    <strong>Valor total do orçamento:</strong>{' '}
                    <span>{this.formatarMoeda(orcamento.valor || 0)}</span>
                </Typography>
                <Typography className={classes.bodyTextWithMargin}>
                    <strong>Observações:</strong> {orcamento.observacoes || '-'}
                </Typography>

                {orcamentoContent}

                {/* Email alert for client view */}
                {this.renderEmailAlert()}

                {/* Client accept/refuse buttons */}
                {modo === 'cliente' && this.renderClientButtons()}


            </Box>
        );
    }
}

export default withStyles(styles)(DetailsOrcamentoSection);
